// COPIA da TMA#718 di CriGio @ 4638c540: src/components/burraco/TavoloVerticale.tsx (costanti 43-70, calcolo della mano 315-345)
// Cambi: funzione pura (nel Burraco il conto sta nel corpo del componente), `larghezza` e misure della carta come parametri, e TRE valori propri del CP, dichiarati qui sotto:
//   · `PASSO_MINIMO` 15 (Burraco: 22): il Burraco non ha mai piu' di ~21 carte da 50 px e due file; la Fantasyland ne ha 14-17 e il dispaccio I-794 vuole UNA fila a 360 px.
//     Con carte da 36 px il passo di 17 carte su 320 px di mano (un telefono da 360) e' 16: l'indice (rango e seme, in alto a sinistra) e' largo ~16, quindi resta leggibile;
//   · l'altezza della mano non cresce MAI oltre quella della carta di base (`altezzaMano` = `ch0`): il tavolo in piedi non ha un pixel da cedere (banco di tma-orc-1, 604/604 a 360×780);
//     le carte che crescono (con poche carte in mano) si tagliano in basso come nel Burraco, se ne vede almeno `FRAZIONE_VISIBILE`;
//   · niente prospettiva (`INCLINAZIONE_MANO`, `perspective` del Burraco): non c'e' la regola CSS e non e' nel dispaccio; la carta scelta si raddrizza e si alza col suo stile (`PlayingCard`),
//     non di `SOLLEVA` come nel Burraco.

/** Sotto questo passo (px fra una carta e la successiva) le carte si sovrappongono troppo per leggerle: la mano va su DUE file. Burraco: 22. */
export const PASSO_MINIMO = 15;
/** Quanto si sovrappongono le carte quando c'e' spazio: la larghezza ideale di una carta fa stare la mano con un passo di poco piu' di meta carta. */
export const SOVRAPPOSIZIONE = 0.55;
/** L'inclinazione massima di una carta all'estremo, quanto scendono le carte ai bordi (l'arco, come frazione dell'altezza di base: 12 px su 70) e l'inclinazione per carta. */
export const VENTAGLIO_GRADI = 13;
export const VENTAGLIO_ARCO = 12 / 70;
export const GRADI_PER_CARTA = 3.2;
/** Una carta grande si puo' tagliare in basso: se ne vede almeno questa frazione (rango e seme stanno nel primo 55%). */
export const FRAZIONE_VISIBILE = 0.66;
const SIN_VENTAGLIO = Math.sin((VENTAGLIO_GRADI * Math.PI) / 180);
/** Il margine che il ventaglio lascia ai due lati per le carte inclinate (px, per una carta alta `ch`): chi ne sposta il contenitore (lo scarto, accanto al suo messaggio) lo toglie. */
export const margineVentaglio = (ch: number): number => Math.ceil(ch * SIN_VENTAGLIO) + 2;

export interface OpzioniVentaglio {
  /** Quante carte. */
  n: number;
  /** La larghezza (px) in cui deve stare la mano. */
  larghezza: number;
  /** La carta di base (px): quella che la mano ha con la mano piena. */
  cw0: number;
  ch0: number;
  /** La larghezza massima (px) a cui possono CRESCERE le carte con poche carte in mano. */
  massima: number;
  passoMinimo?: number;
  /** L'arco del ventaglio (frazione dell'altezza della carta di base: le carte ai bordi scendono di tanto). Senza, `VENTAGLIO_ARCO`; 0 = le carte stanno tutte alla stessa altezza (inclinate, ma senza arco). */
  arco?: number;
}

export interface PosizioneCarta {
  /** Quale delle file (0 = la bassa, quella che sta sopra le altre). */
  fila: number;
  /** Spostamento e rotazione del contenitore (px, gradi): sono da applicare con `transform-origin: 50% 100%`. */
  x: number;
  y: number;
  angolo: number;
}

export interface Ventaglio {
  /** 1 o 2 file. Due solo se una fila avrebbe un passo sotto `passoMinimo`. */
  file: number;
  perFila: number;
  /** Scala della carta rispetto a quella di base (>= 1). */
  scala: number;
  cw: number;
  ch: number;
  passo: number;
  /** L'altezza (px) del contenitore della mano. */
  altezzaMano: number;
  /** Il passo che UNA fila avrebbe avuto: e' quello che si confronta con `passoMinimo`. */
  passoUnaFila: number;
  posizione: (i: number) => PosizioneCarta;
}

export function disponiVentaglio({ n, larghezza, cw0, ch0, massima, passoMinimo = PASSO_MINIMO, arco: arcoFrazione = VENTAGLIO_ARCO }: OpzioniVentaglio): Ventaglio {
  // meno carte = carte piu' grandi (fino a un massimo), cosi' la mano riempie la larghezza invece di restare un mucchietto al centro
  const margineBase = Math.ceil(ch0 * SIN_VENTAGLIO) + 2;
  const utileBase = Math.max(larghezza - 2 * margineBase, cw0);
  const larghezzaIdeale = n > 0 ? utileBase / (1 + SOVRAPPOSIZIONE * (n - 1)) : cw0;
  const scala = Math.min(Math.max(larghezzaIdeale, cw0), Math.max(massima, cw0)) / cw0;
  const cw = cw0 * scala;
  const ch = ch0 * scala;
  const margine = Math.ceil(ch * SIN_VENTAGLIO) + 2;
  const larghezzaUtile = Math.max(larghezza - 2 * margine, cw);
  const passoUnaFila = n > 1 ? (larghezzaUtile - cw) / (n - 1) : 0;
  const file = n > 1 && passoUnaFila < passoMinimo ? 2 : 1;
  const perFila = Math.ceil(n / file);
  const passo = perFila > 1 ? Math.min(cw * 0.8, (larghezzaUtile - cw) / (perFila - 1)) : 0;
  const arco = ch0 * arcoFrazione;
  // la seconda fila e' coperta dalla prima: della fila alta se ne vede la parte dei due indici (la meta' alta)
  const scarto2 = ch0 * 0.66;
  const altezzaMano = file === 2 ? ch0 + scarto2 : ch0;
  const posizione = (i: number): PosizioneCarta => {
    const fila = file === 2 ? Math.floor(i / perFila) : 0;
    const col = file === 2 ? i % perFila : i;
    const inFila = file === 2 && fila === file - 1 ? n - perFila * (file - 1) : perFila;
    const x0 = margine + (larghezzaUtile - (cw + (inFila - 1) * passo)) / 2;
    const meta = (inFila - 1) / 2;
    const d = meta > 0 ? (col - meta) / meta : 0; // da -1 (sinistra) a +1 (destra)
    const gradi = Math.min(VENTAGLIO_GRADI, GRADI_PER_CARTA * meta);
    return { fila, x: x0 + col * passo, y: fila * scarto2 + d * d * arco, angolo: d * gradi };
  };
  return { file, perFila, scala, cw, ch, passo, altezzaMano, passoUnaFila, posizione };
}
